import type { SentMoodboard } from '@shared/moodboard/send'
import type { Crop } from '@shared/moodboard/layout'
import { miroFont } from '@shared/data/miroFonts'
import { turnedSource } from '@shared/moodboard/turn'
import { API_BASE, authHeaders } from '@shared/services/apiAuth'
import { encodeCut } from '@shared/moodboard/float'

/**
 * The mood board as one picture (Huanrui, 2026-10-12: Copy to project)
 * -------------------------------------------------------------
 * Drawn here, in the browser, exactly as the sheet shows it -- ground,
 * pictures cut as they are cut, title, words, colour strip, credit line --
 * at twice the sheet's size (3840 x 2160), and handed back as a JPEG for a
 * project's board.
 */

export interface DrawnPicture {
  url: string
  x: number
  y: number
  w: number
  h: number
  crop: Crop
  /** Degrees clockwise; the crop is of the picture turned (shared/moodboard/turn.ts). */
  turn?: number
}

const SHEET_W = 1920
const SHEET_H = 1080

export async function bitmapOf(url: string): Promise<ImageBitmap> {
  let response: Response | null = null
  try {
    response = await fetch(url)
  } catch {
    response = null
  }
  // Unsplash and Pexels refusing to be read from here: the Worker fetches it.
  if ((!response || !response.ok) && /^https:\/\/images\.(unsplash|pexels)\.com\//.test(url)) {
    response = await fetch(`${API_BASE}/api/stock/image?u=${encodeURIComponent(url)}`, { headers: await authHeaders() })
  }
  if (!response || !response.ok) throw new Error('A picture could not be read')
  return createImageBitmap(await response.blob())
}

export async function drawMoodboardImage(
  board: Pick<SentMoodboard, 'background' | 'texts' | 'swatches'>,
  pictures: DrawnPicture[],
  scale = 2,
): Promise<Blob> {
  const canvas = document.createElement('canvas')
  canvas.width = SHEET_W * scale
  canvas.height = SHEET_H * scale
  const pen = canvas.getContext('2d')
  if (!pen) throw new Error('Canvas is unavailable')
  pen.fillStyle = board.background || '#ffffff'
  pen.fillRect(0, 0, canvas.width, canvas.height)
  pen.imageSmoothingQuality = 'high'

  // The pictures, four at a time.
  const queue = [...pictures]
  const worker = async () => {
    for (let one = queue.shift(); one; one = queue.shift()) {
      const bitmap = await bitmapOf(one.url)
      try {
        const turned = turnedSource(bitmap, one.turn)
        pen.drawImage(
          turned.source,
          one.crop.x * turned.width,
          one.crop.y * turned.height,
          Math.max(1, one.crop.w * turned.width),
          Math.max(1, one.crop.h * turned.height),
          one.x * scale,
          one.y * scale,
          one.w * scale,
          one.h * scale,
        )
      } finally {
        bitmap.close()
      }
    }
  }
  await Promise.all(Array.from({ length: Math.min(4, queue.length) }, worker))

  for (const swatch of board.swatches) {
    pen.fillStyle = swatch.color
    pen.fillRect(swatch.x * scale, swatch.y * scale, swatch.w * scale, swatch.h * scale)
  }

  for (const text of board.texts) {
    const size = text.style.size * scale
    const font = `${text.style.bold ? 700 : 400} ${size}px ${miroFont(text.style.font).css}`
    try {
      await document.fonts.load(font, text.text)
    } catch {
      /* drawn in the fallback typeface */
    }
    pen.save()
    pen.beginPath()
    pen.rect(text.x * scale, text.y * scale, text.w * scale, text.h * scale)
    pen.clip()
    pen.font = font
    pen.fillStyle = text.style.color
    pen.textBaseline = 'middle'
    pen.textAlign = text.style.align
    const x = text.style.align === 'center' ? text.x + text.w / 2 : text.style.align === 'right' ? text.x + text.w : text.x
    pen.fillText(text.text, x * scale, (text.y + text.h / 2) * scale)
    pen.restore()
  }

  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('The picture could not be made'))), 'image/jpeg', 0.92),
  )
}

/**
 * One picture cut as the sheet shows it, at about the size it will have on
 * the Miro board (the sheet doubled), for a faster Send to Miro
 * (workers/api/src/moodboardTemp.ts). JPEG, or PNG for a PNG.
 */
export async function cutForBoard(picture: DrawnPicture, fullSize = false): Promise<Blob> {
  const bitmap = await bitmapOf(picture.url)
  try {
    const turned = turnedSource(bitmap, picture.turn)
    const sw = Math.max(1, picture.crop.w * turned.width)
    const sh = Math.max(1, picture.crop.h * turned.height)
    // Full size: every pixel of the part shown, nothing scaled down (a frame
    // from a project's board, 2026-10-13).
    const want = fullSize ? Infinity : Math.min(2400, Math.max(picture.w, picture.h) * 2)
    const scale = Math.min(1, want / Math.max(sw, sh))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(sw * scale))
    canvas.height = Math.max(1, Math.round(sh * scale))
    const pen = canvas.getContext('2d')
    if (!pen) throw new Error('Canvas is unavailable')
    pen.imageSmoothingQuality = 'high'
    pen.drawImage(turned.source, picture.crop.x * turned.width, picture.crop.y * turned.height, sw, sh, 0, 0, canvas.width, canvas.height)
    // A cut-out stays a see-through PNG; a translucent ground is made solid
    // in its own colours; anything else on white -- as JPEGs (float.ts, 2026-10-23).
    return await encodeCut(canvas, fullSize ? 0.95 : 0.9)
  } finally {
    bitmap.close()
  }
}
